import * as React from 'react'

import { LOCIZE_NAMESPACE } from 'main/common'
import { i18nKeyPrefix } from 'main/pages/Search/i18n'
import { Strong, Text, Visible } from 'main/ui'
import { IRangeProps } from 'main/ui/slider/RangeSlider'
import RangeSlider from 'main/ui/slider/RangeSlider'
import { ageRangeLabelsAndValues, IFilters } from 'main/util/search'
import { useTranslation } from 'react-i18next'
import {
  Apply,
  Clear,
  FieldCopyContainer,
  FieldsContainer,
  FilterFormContainer,
  FilterFormSubmitContainer,
} from '../../common/FilterForm.styles'
import { ISearchFormProps } from '../../types'

const minValue = 0
const maxValue = ageRangeLabelsAndValues.length - 1

const PRE = `${i18nKeyPrefix}-FILTERS-AGE-RANGE-FORM`

export default function FilterForm(props: ISearchFormProps) {
  const { filters, onChangeFilters, onRequestCloseForm } = props
  const [params, setParams] = React.useState<IFilters>({ ...filters })
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <FilterFormContainer>
      <Fields params={params} onChangeParams={setParams} />
      <Visible lg xl>
        <FilterFormSubmitContainer>
          <Clear data-pendo="availabilityFilter-clear" onClick={() => handleClear()}>
            {t(`${PRE}-CLEAR`)}
          </Clear>
          <Apply data-pendo="availabilityFilter-apply" onClick={() => handleApply()}>
            {t(`${PRE}-APPLY`)}
          </Apply>
        </FilterFormSubmitContainer>
      </Visible>
    </FilterFormContainer>
  )

  function handleApply() {
    onRequestCloseForm()
    onChangeFilters({
      ...filters,
      enrollingAgeMonthsMin: params.enrollingAgeMonthsMin,
      enrollingAgeMonthsMax: params.enrollingAgeMonthsMax,
    })
  }

  function handleClear() {
    setParams({ ...params, enrollingAgeMonthsMin: null, enrollingAgeMonthsMax: null })
  }
}

interface IFieldsProps {
  params: IFilters
  onChangeParams: (params: IFilters) => void
}

export function Fields(fieldsProps: IFieldsProps) {
  const { params, onChangeParams } = fieldsProps
  const minValueOrDefault = params.enrollingAgeMonthsMin ? params.enrollingAgeMonthsMin : minValue
  const maxValueOrDefault = params.enrollingAgeMonthsMax ? params.enrollingAgeMonthsMax : maxValue
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <FieldCopyContainer>
      <Strong> {t(`${PRE}-LABEL`)} </Strong>
      <div>
        <Text> {RangeLabel(minValueOrDefault, maxValueOrDefault)} </Text>
      </div>
      <FieldsContainer>
        <RangeSlider
          range={{ min: minValue, max: maxValue }}
          step={1}
          value={{
            max: maxValueOrDefault,
            min: minValueOrDefault,
          }}
          onChange={value => handleChange(value)}
        />
      </FieldsContainer>
    </FieldCopyContainer>
  )

  function handleChange(value: IRangeProps) {
    const enrollingAgeMonthsMin = value.min === minValue ? null : value.min
    const enrollingAgeMonthsMax = value.max === maxValue ? null : value.max

    onChangeParams({ ...params, enrollingAgeMonthsMin, enrollingAgeMonthsMax })
  }

  function RangeLabel(minAge: number, maxAge: number) {
    const lastIndex = ageRangeLabelsAndValues.length - 1
    const min = typeof minAge === 'number' ? minAge : 0
    const max = typeof maxAge === 'number' ? maxAge : lastIndex

    const lowAge = t(ageRangeLabelsAndValues[min]?.key, { qty: ageRangeLabelsAndValues[min]?.qty })
    const highAge = t(ageRangeLabelsAndValues[max]?.key, { qty: ageRangeLabelsAndValues[max]?.qty })

    return (
      <Text>
        {lowAge} - {highAge}
      </Text>
    )
  }
}
